import { useEffect, useState } from 'react';
import api from '../services/api';
import { useWebSocket } from '../contexts/WebSocketContext';
import type { WebSocketEvent } from '../types';

/**
 * Whether the current user's network access is enabled. Drives the
 * "Fetch SERP" buttons across the app — when the network is disabled the
 * crawl backend (POST /serps/requests) rejects the request, so we disable
 * the trigger rather than let the user fire a request that 403s.
 *
 * Optimistically returns `true` until the initial fetch resolves so we
 * don't flicker-disable on load; the backend remains the hard gate. Flips
 * to `false` immediately on a `network_disabled` websocket event (the same
 * event Layout uses for its banner) so a mid-session suspension disables
 * the buttons without a reload.
 */
export function useNetworkEnabled(): boolean {
  const [enabled, setEnabled] = useState(true);
  const { subscribe } = useWebSocket();

  useEffect(() => {
    let live = true;
    api
      .getNetworkSettings()
      .then((s) => {
        if (live) setEnabled(s.is_network_enabled);
      })
      .catch(() => {
        // Leave optimistic `true`; the backend still enforces the gate.
      });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    return subscribe((event: WebSocketEvent) => {
      if (event.event_type === 'network_disabled') setEnabled(false);
    });
  }, [subscribe]);

  return enabled;
}
